import { DialogCloseEvent } from './dialog.js';
import { clamp, preventDefault, isTouchDevice } from './utilities.js';
import { Component } from '@theme/component';

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const DEFAULT_ZOOM = 1.5;
const DOUBLE_TAP_DELAY = 300;
const DOUBLE_TAP_DISTANCE = 50;
const DRAG_THRESHOLD = 10;
const KEYBOARD_PAN_STEP = 48;

/**
 * @typedef {object} Refs
 * @property {HTMLImageElement} image - The image element to zoom and drag.
 */

/** @extends {Component<Refs>} */
export class DragZoomWrapper extends Component {
  requiredRefs = ['image'];
  #controller = new AbortController();
  /** @type {number} */
  #scale = MIN_ZOOM;
  /** @type {number} */
  #initialDistance = 0;
  /** @type {number} */
  #startScale = MIN_ZOOM;
  /** @type {Point} */
  #translate = { x: 0, y: 0 };
  /** @type {Point} */
  #startPosition = { x: 0, y: 0 };
  /** @type {Point} */
  #startTranslate = { x: 0, y: 0 };
  /** @type {boolean} */
  #isDragging = false;
  /** @type {boolean} */
  #initialized = false;
  /** @type {number | null} */
  #animationFrame = null;
  /** @type {number} */
  #lastTapTime = 0;
  /** @type {Point | null} */
  #lastTapPosition = null;

  /** @type {boolean} */
  #hasDraggedBeyondThreshold = false;

  /** @type {boolean} */
  #hasManualZoom = false;

  /** Ignores the click that follows a pointer drag-to-pan */
  /** @type {boolean} */
  #suppressClick = false;

  /** @type {number | null} */
  #activePointerId = null;

  /**
   * Sticky zoom from keyboard/click — not cleared on mouseleave.
   * Hover zoom is temporary and clears when the pointer leaves the photo.
   * @type {boolean}
   */
  #stickyZoom = false;

  get #image() {
    return this.refs.image;
  }

  get #isZoomed() {
    return this.#scale > MIN_ZOOM + 0.05;
  }

  /** Fine pointer + hover capability (desktop mouse), independent of viewport width */
  get #supportsHoverZoom() {
    return !isTouchDevice();
  }

  connectedCallback() {
    super.connectedCallback();
    if (!this.#image) return;

    this.#initResizeListener();
    window.addEventListener(DialogCloseEvent.eventName, this.#resetZoom);

    // Desktop and mobile both use this component (hover/keyboard on fine pointers,
    // touch pinch/double-tap on touch devices).
    this.#initEventListeners();
    this.#updateZoomState();
    this.#updateTransform();
  }

  #initResizeListener() {
    this.#resizeObserver.observe(this);
  }

  #initEventListeners() {
    if (this.#initialized) return;
    this.#initialized = true;
    const { signal } = this.#controller;
    const touchOptions = { passive: false, signal };

    this.addEventListener('touchstart', this.#handleTouchStart, touchOptions);
    this.addEventListener('touchmove', this.#handleTouchMove, touchOptions);
    this.addEventListener('touchend', this.#handleTouchEnd, touchOptions);

    this.addEventListener('pointerdown', this.#handlePointerDown, { signal });
    this.addEventListener('pointermove', this.#handlePointerMove, { signal });
    this.addEventListener('pointerup', this.#handlePointerUp, { signal });
    this.addEventListener('pointercancel', this.#handlePointerUp, { signal });
    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('keydown', this.#handleKeyDown, { signal });

    if (this.#supportsHoverZoom) {
      this.addEventListener('mousemove', this.#handleMouseMove, { signal });
      this.addEventListener('mouseleave', this.#handleMouseLeave, { signal });
    }

    // Initialize transform immediately
    this.#updateTransform();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener(DialogCloseEvent.eventName, this.#resetZoom);
    this.#controller.abort();
    this.#resizeObserver.disconnect();
    this.#cancelAnimationFrame();
  }

  #handleResize = () => {
    if (!this.#initialized) {
      this.#initEventListeners();
    }

    if (this.#initialized) {
      this.#requestUpdateTransform();
    }
  };

  #resizeObserver = new ResizeObserver(this.#handleResize);

  /**
   * @param {TouchEvent} event
   */
  #handleTouchStart = (event) => {
    preventDefault(event);

    const touchCount = event.touches.length;

    if (touchCount === 2) {
      // Early exit if touches are invalid
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      if (!touch1 || !touch2) return;

      // Avoid object allocation by passing touches directly
      this.#startZoomGestureFromTouches(touch1, touch2);
    } else if (touchCount === 1) {
      const touch = event.touches[0];
      if (!touch) return;

      // Use performance.now() for better precision and performance
      const currentTime = performance.now();
      const timeSinceLastTap = currentTime - this.#lastTapTime;

      // Early exit if too much time has passed
      if (timeSinceLastTap >= DOUBLE_TAP_DELAY) {
        this.#storeTapInfo(currentTime, touch);
        this.#startDragGestureFromPoint(touch.clientX, touch.clientY);
        return;
      }

      // Only check distance if we have a previous tap within time window
      if (this.#lastTapPosition) {
        const distance = getDistance(touch, this.#lastTapPosition);

        if (distance < DOUBLE_TAP_DISTANCE) {
          // This is a double-tap, handle zoom toggle
          this.#stickyZoom = true;
          this.#toggleZoomAt(touch.clientX, touch.clientY);
          this.#lastTapTime = 0; // Reset to prevent triple-tap
          this.#lastTapPosition = null;
          return;
        }
      }

      // Store tap info for potential double-tap detection
      this.#storeTapInfo(currentTime, touch);
      this.#startDragGestureFromPoint(touch.clientX, touch.clientY);
    }
  };

  /**
   * Start a zoom gesture with two touches
   * @param {Touch} touch1
   * @param {Touch} touch2
   */
  #startZoomGestureFromTouches(touch1, touch2) {
    // Calculate initial distance between touches
    this.#initialDistance = getDistance(touch1, touch2);
    this.#startScale = this.#scale;
    this.#isDragging = false;
  }

  /**
   * Start a drag gesture from a pointer/touch position
   * @param {number} clientX
   * @param {number} clientY
   */
  #startDragGestureFromPoint(clientX, clientY) {
    this.#startPosition = { x: clientX, y: clientY };
    this.#startTranslate = { x: this.#translate.x, y: this.#translate.y };
    this.#isDragging = true;
    this.#hasDraggedBeyondThreshold = false;
  }

  /**
   * Store tap information for double-tap detection
   * @param {number} currentTime
   * @param {Touch} touch
   */
  #storeTapInfo(currentTime, touch) {
    this.#lastTapTime = currentTime;
    this.#lastTapPosition = { x: touch.clientX, y: touch.clientY };
  }

  /**
   * Toggle between 1x (whole image) and DEFAULT_ZOOM, optionally centering on a point.
   * Used by double-tap, click, and Enter/Space.
   * @param {number} [clientX]
   * @param {number} [clientY]
   */
  #toggleZoomAt(clientX, clientY) {
    const rect = this.getBoundingClientRect();
    const containerCenter = {
      x: this.clientWidth / 2,
      y: this.clientHeight / 2,
    };

    const pointX = clientX ?? rect.left + containerCenter.x;
    const pointY = clientY ?? rect.top + containerCenter.y;

    let targetZoom;

    // If already zoomed (or pinch was used), reset to 1x
    if (this.#hasManualZoom || this.#isZoomed) {
      targetZoom = MIN_ZOOM;
      this.#hasManualZoom = false;
      this.#stickyZoom = false;
      this.#translate = { x: 0, y: 0 }; // Center the image
      this.classList.remove('is-hover-panning');
    } else {
      // Toggle to default zoom and keep it sticky (survives mouseleave)
      targetZoom = DEFAULT_ZOOM;
      this.#stickyZoom = true;
      this.classList.remove('is-hover-panning');
    }

    // If we're not going to 1x, adjust translation to center zoom on the point
    if (targetZoom !== MIN_ZOOM) {
      const oldScale = this.#scale;
      this.#scale = Math.min(MAX_ZOOM, targetZoom);

      const distanceFromCenter = {
        x: pointX - rect.left - containerCenter.x,
        y: pointY - rect.top - containerCenter.y,
      };

      const scaleDelta = this.#scale / oldScale - 1.0;
      this.#translate.x -= (distanceFromCenter.x * scaleDelta) / this.#scale;
      this.#translate.y -= (distanceFromCenter.y * scaleDelta) / this.#scale;
    } else {
      this.#scale = targetZoom;
      this.#translate = { x: 0, y: 0 };
    }

    this.#updateZoomState();
    this.#requestUpdateTransform();
  }

  /**
   * Layout box of the visible photo (object-fit: contain), ignoring CSS transform zoom.
   * Used so hover-zoom only activates when the cursor is on the picture, not letterbox padding.
   * @returns {{ left: number, top: number, width: number, height: number } | null}
   */
  #getContainedImageRect() {
    const wrapperRect = this.getBoundingClientRect();
    if (!wrapperRect.width || !wrapperRect.height || !this.#image) return null;

    const imageElement = this.#image;
    let naturalWidth = imageElement.naturalWidth;
    let naturalHeight = imageElement.naturalHeight;

    if (!naturalWidth || !naturalHeight) {
      naturalWidth = wrapperRect.width;
      naturalHeight = wrapperRect.height;
    }

    const imageAspectRatio = naturalWidth / naturalHeight;
    const wrapperAspectRatio = wrapperRect.width / wrapperRect.height;

    let width;
    let height;

    if (imageAspectRatio > wrapperAspectRatio) {
      // Image is wider — width fits, height is letterboxed
      width = wrapperRect.width;
      height = width / imageAspectRatio;
    } else {
      // Image is taller — height fits, width is letterboxed
      height = wrapperRect.height;
      width = height * imageAspectRatio;
    }

    return {
      left: wrapperRect.left + (wrapperRect.width - width) / 2,
      top: wrapperRect.top + (wrapperRect.height - height) / 2,
      width,
      height,
    };
  }

  /**
   * @param {number} clientX
   * @param {number} clientY
   */
  #isPointerOverImage(clientX, clientY) {
    const imageRect = this.#getContainedImageRect();
    if (!imageRect) return false;

    return (
      clientX >= imageRect.left &&
      clientX <= imageRect.left + imageRect.width &&
      clientY >= imageRect.top &&
      clientY <= imageRect.top + imageRect.height
    );
  }

  /**
   * Hover zoom: scale up and pan so the point under the cursor stays in view.
   * Same image element (not a popup) — pointer can move on it without the zoom disappearing.
   * @param {number} clientX
   * @param {number} clientY
   */
  #hoverZoomTo(clientX, clientY) {
    const wasZoomed = this.#isZoomed;

    this.#scale = DEFAULT_ZOOM;
    this.#hasManualZoom = false;

    const imageRect = this.#getContainedImageRect();
    if (!imageRect) return;

    const xRatio = clamp((clientX - imageRect.left) / imageRect.width, 0, 1);
    const yRatio = clamp((clientY - imageRect.top) / imageRect.height, 0, 1);

    const { maxTranslateX, maxTranslateY } = this.#getMaxTranslate();
    this.#translate.x = (0.5 - xRatio) * 2 * maxTranslateX;
    this.#translate.y = (0.5 - yRatio) * 2 * maxTranslateY;

    // Longer ease on zoom-in; shorter while panning so the lens tracks the cursor
    this.classList.toggle('is-hover-panning', wasZoomed);
    this.#updateZoomState();
    this.#requestUpdateTransform();
  }

  #clearHoverZoom() {
    if (this.#stickyZoom || !this.#isZoomed) return;

    this.classList.remove('is-hover-panning');
    this.#scale = MIN_ZOOM;
    this.#translate = { x: 0, y: 0 };
    this.#updateZoomState();
    this.#requestUpdateTransform();
  }

  #handleMouseMove = (event) => {
    if (!this.#supportsHoverZoom) return;
    if (this.#isDragging) return;
    if (this.#stickyZoom) return;

    // Activate / keep zoom only when the cursor is on the picture
    if (!this.#isPointerOverImage(event.clientX, event.clientY)) {
      this.#clearHoverZoom();
      return;
    }

    this.#hoverZoomTo(event.clientX, event.clientY);
  };

  #handleMouseLeave = () => {
    if (!this.#supportsHoverZoom || this.#stickyZoom) return;
    this.#clearHoverZoom();
  };

  /**
   * @param {TouchEvent} event
   */
  #handleTouchMove = (event) => {
    preventDefault(event);

    const touchCount = event.touches.length;

    if (touchCount === 2) {
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      if (touch1 && touch2) {
        this.#processZoomGesture(touch1, touch2);
      }
    } else if (touchCount === 1 && this.#isDragging) {
      const touch = event.touches[0];
      if (touch) {
        this.#processDragGesture(touch.clientX, touch.clientY);
      }
    }
  };

  /**
   * Process zoom gesture from touches
   * @param {Touch} touch1
   * @param {Touch} touch2
   */
  #processZoomGesture(touch1, touch2) {
    // Calculate midpoint directly without object allocation
    const midX = (touch1.clientX + touch2.clientX) / 2;
    const midY = (touch1.clientY + touch2.clientY) / 2;

    // Calculate current distance between touches
    const currentDistance = getDistance(touch1, touch2);
    const oldScale = this.#scale;

    // Calculate and apply new scale
    const newScale = (currentDistance / this.#initialDistance) * this.#startScale;
    this.#scale = clamp(newScale, MIN_ZOOM, MAX_ZOOM);

    // Mark that manual zoom has been used
    this.#hasManualZoom = this.#isZoomed;
    this.#stickyZoom = this.#isZoomed;

    // Adjust translation to keep the pinch midpoint stationary
    const containerCenterX = this.clientWidth / 2;
    const containerCenterY = this.clientHeight / 2;
    const distanceFromCenterX = midX - containerCenterX;
    const distanceFromCenterY = midY - containerCenterY;
    const scaleDelta = this.#scale / oldScale - 1.0;

    this.#translate.x -= (distanceFromCenterX * scaleDelta) / this.#scale;
    this.#translate.y -= (distanceFromCenterY * scaleDelta) / this.#scale;

    this.#updateZoomState();
    this.#requestUpdateTransform();
    this.#isDragging = false;
  }

  /**
   * Process drag gesture from a pointer/touch position
   * @param {number} clientX
   * @param {number} clientY
   */
  #processDragGesture(clientX, clientY) {
    if (!this.#isZoomed) return;

    const distance = getDistance({ x: clientX, y: clientY }, this.#startPosition);

    if (!this.#hasDraggedBeyondThreshold && distance < DRAG_THRESHOLD) {
      // Movement is too small, don't process as drag yet
      return;
    }

    this.#hasDraggedBeyondThreshold = true;

    const dx = clientX - this.#startPosition.x;
    const dy = clientY - this.#startPosition.y;

    this.#translate.x = this.#startTranslate.x + dx / this.#scale;
    this.#translate.y = this.#startTranslate.y + dy / this.#scale;

    this.#requestUpdateTransform();
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchEnd = (event) => {
    if (event.touches.length === 0) {
      this.#isDragging = false;
      this.#requestUpdateTransform();
      this.#hasDraggedBeyondThreshold = false;
    }
  };

  /**
   * Mouse / pen: drag to pan when zoomed. Touch pinch/pan is handled separately.
   * @param {PointerEvent} event
   */
  #handlePointerDown = (event) => {
    if (event.pointerType === 'touch') return;
    if (event.button !== 0) return;
    if (!this.#isZoomed) return;

    this.#activePointerId = event.pointerId;
    this.classList.add('is-dragging');
    this.#startDragGestureFromPoint(event.clientX, event.clientY);
    this.setPointerCapture(event.pointerId);
  };

  /**
   * @param {PointerEvent} event
   */
  #handlePointerMove = (event) => {
    if (event.pointerType === 'touch') return;
    if (this.#activePointerId !== event.pointerId || !this.#isDragging) return;

    this.#processDragGesture(event.clientX, event.clientY);
  };

  /**
   * @param {PointerEvent} event
   */
  #handlePointerUp = (event) => {
    if (event.pointerType === 'touch') return;
    if (this.#activePointerId !== event.pointerId) return;

    if (this.#hasDraggedBeyondThreshold) {
      this.#suppressClick = true;
    }

    this.#activePointerId = null;
    this.#isDragging = false;
    this.classList.remove('is-dragging');

    if (this.hasPointerCapture(event.pointerId)) {
      this.releasePointerCapture(event.pointerId);
    }

    this.#requestUpdateTransform();
  };

  /**
   * Click toggles sticky zoom (same idea as mobile double-tap / Enter).
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (this.#suppressClick) {
      this.#suppressClick = false;
      this.#hasDraggedBeyondThreshold = false;
      return;
    }

    this.focus({ preventScroll: true });
    this.#toggleZoomAt(event.clientX, event.clientY);
  };

  /**
   * Enter/Space toggles sticky zoom; arrow keys zoom in (if needed) then pan.
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      this.#toggleZoomAt();
      return;
    }

    if (!this.#isZoomed) {
      if (
        event.key === 'ArrowLeft' ||
        event.key === 'ArrowRight' ||
        event.key === 'ArrowUp' ||
        event.key === 'ArrowDown'
      ) {
        event.preventDefault();
        event.stopPropagation();
        this.#stickyZoom = true;
        this.#scale = DEFAULT_ZOOM;
        this.#updateZoomState();
        this.#requestUpdateTransform();
      }
      return;
    }

    const panKeys = {
      ArrowLeft: { x: KEYBOARD_PAN_STEP, y: 0 },
      ArrowRight: { x: -KEYBOARD_PAN_STEP, y: 0 },
      ArrowUp: { x: 0, y: KEYBOARD_PAN_STEP },
      ArrowDown: { x: 0, y: -KEYBOARD_PAN_STEP },
    };

    const delta = panKeys[/** @type {keyof typeof panKeys} */ (event.key)];
    if (!delta) return;

    event.preventDefault();
    event.stopPropagation();

    this.#translate.x += delta.x / this.#scale;
    this.#translate.y += delta.y / this.#scale;
    this.#requestUpdateTransform();
  };

  #updateZoomState() {
    const zoomed = this.#isZoomed;
    this.classList.toggle('is-zoomed', zoomed);
    this.setAttribute('aria-pressed', String(zoomed));
  }

  /**
   * Max pan distance at the current scale (object-fit: contain).
   * @returns {{ maxTranslateX: number, maxTranslateY: number }}
   */
  #getMaxTranslate() {
    const wrapperRect = this.getBoundingClientRect();
    if (!wrapperRect.width || !wrapperRect.height || !this.#image) {
      return { maxTranslateX: 0, maxTranslateY: 0 };
    }

    const imageElement = this.#image;
    let naturalWidth;
    let naturalHeight;

    // Try to get natural dimensions
    if (imageElement.naturalWidth > 0 && imageElement.naturalHeight > 0) {
      naturalWidth = imageElement.naturalWidth;
      naturalHeight = imageElement.naturalHeight;
    } else {
      // Fallback: assume square image if we can't get natural dimensions
      naturalWidth = wrapperRect.width;
      naturalHeight = wrapperRect.width;
    }

    // Calculate how the image fits within the wrapper (object-fit: contain)
    const imageAspectRatio = naturalWidth / naturalHeight;
    const wrapperAspectRatio = wrapperRect.width / wrapperRect.height;

    let actualImageWidth;
    let actualImageHeight;

    if (imageAspectRatio > wrapperAspectRatio) {
      // Image is wider — width fits exactly, height is smaller (letterboxed top/bottom)
      actualImageWidth = wrapperRect.width;
      actualImageHeight = actualImageWidth / imageAspectRatio;
    } else {
      // Image is taller — height fits exactly, width is smaller (letterboxed left/right)
      actualImageHeight = wrapperRect.height;
      actualImageWidth = actualImageHeight * imageAspectRatio;
    }

    const scale = clamp(this.#scale, MIN_ZOOM, MAX_ZOOM);
    const scaledImageWidth = actualImageWidth * scale;
    const scaledImageHeight = actualImageHeight * scale;
    const horizontalOverflow = Math.max(0, scaledImageWidth - wrapperRect.width);
    const verticalOverflow = Math.max(0, scaledImageHeight - wrapperRect.height);

    // Max translation is half the overflow (image starts centered)
    return {
      maxTranslateX: horizontalOverflow / 2 / scale,
      maxTranslateY: verticalOverflow / 2 / scale,
    };
  }

  /**
   * Constrain image translation to keep it within the viewport
   */
  #constrainTranslation() {
    const containerWidth = this.clientWidth;
    const containerHeight = this.clientHeight;
    if (!containerWidth || !containerHeight || !this.#image) return;

    // Keep scale between MIN_ZOOM (1) and MAX_ZOOM (5)
    this.#scale = clamp(this.#scale, MIN_ZOOM, MAX_ZOOM);

    // At minimum zoom (1x), the full image should be visible with no dragging allowed
    if (this.#scale <= MIN_ZOOM) {
      this.#translate.x = 0;
      this.#translate.y = 0;
      return;
    }

    const { maxTranslateX, maxTranslateY } = this.#getMaxTranslate();

    // Apply symmetric constraints (object-fit: contain behavior)
    this.#translate.x = clamp(this.#translate.x, -maxTranslateX, maxTranslateX);
    this.#translate.y = clamp(this.#translate.y, -maxTranslateY, maxTranslateY);

    // Apply final transforms to CSS
    this.style.setProperty('--drag-zoom-scale', this.#scale.toString());
    this.style.setProperty('--drag-zoom-translate-x', `${this.#translate.x}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.#translate.y}px`);
  }

  /**
   * Request an animation frame to update the transform
   */
  #requestUpdateTransform = () => {
    if (!this.#animationFrame) {
      this.#animationFrame = requestAnimationFrame(this.#updateTransform);
    }
  };

  /**
   * Cancel any pending animation frame
   */
  #cancelAnimationFrame() {
    if (this.#animationFrame) {
      cancelAnimationFrame(this.#animationFrame);
      this.#animationFrame = null;
    }
  }

  #updateTransform = () => {
    this.#animationFrame = null;

    this.#constrainTranslation();
    this.style.setProperty('--drag-zoom-scale', this.#scale.toString());
    this.style.setProperty('--drag-zoom-translate-x', `${this.#translate.x}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.#translate.y}px`);
  };

  /**
   * Public reset used when the dialog closes or the active slide changes.
   */
  reset() {
    this.#resetZoom();
  }

  /**
   * Reset zoom so the whole image is visible (1x, centered).
   * Called when zoom dialog is closed or the slide changes.
   */
  #resetZoom = () => {
    this.#scale = MIN_ZOOM;
    this.#startScale = MIN_ZOOM;
    this.#translate.x = 0;
    this.#translate.y = 0;

    // Reset gesture state to prevent interference on next zoom open
    this.#startPosition = { x: 0, y: 0 };
    this.#startTranslate = { x: 0, y: 0 };
    this.#isDragging = false;
    this.#lastTapTime = 0;
    this.#lastTapPosition = null;
    this.#hasDraggedBeyondThreshold = false;
    this.#hasManualZoom = false;
    this.#suppressClick = false;
    this.#activePointerId = null;
    this.#stickyZoom = false;
    this.classList.remove('is-hover-panning', 'is-dragging');

    this.#updateZoomState();
    this.style.setProperty('--drag-zoom-scale', MIN_ZOOM.toString());
    this.style.setProperty('--drag-zoom-translate-x', '0px');
    this.style.setProperty('--drag-zoom-translate-y', '0px');
  };

  destroy() {
    this.#controller.abort();
    this.#cancelAnimationFrame();
  }
}

/**
 * Calculate distance between two points or touches
 * @param {Point | Touch} point1 - First point or touch
 * @param {Point | Touch} point2 - Second point or touch
 * @returns {number} Distance between the points
 */
function getDistance(point1, point2) {
  // Handle both Point objects (x, y) and Touch objects (clientX, clientY)
  const x1 = /** @type {Point} */ (point1).x ?? /** @type {Touch} */ (point1).clientX;
  const y1 = /** @type {Point} */ (point1).y ?? /** @type {Touch} */ (point1).clientY;
  const x2 = /** @type {Point} */ (point2).x ?? /** @type {Touch} */ (point2).clientX;
  const y2 = /** @type {Point} */ (point2).y ?? /** @type {Touch} */ (point2).clientY;

  const dx = x1 - x2;
  const dy = y1 - y2;
  return Math.sqrt(dx * dx + dy * dy);
}

if (!customElements.get('drag-zoom-wrapper')) {
  customElements.define('drag-zoom-wrapper', DragZoomWrapper);
}

/**
 * @typedef {Object} Point
 * @property {number} x
 * @property {number} y
 */
